<script setup lang="ts">
    import { ref, watch } from 'vue';
    import { useRouter, useRoute } from 'vue-router';
    import type { IssueStatisticsLink } from '../../interfaces';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import List from '@shared/components/List/List.vue';
    import { api } from '@cwd/lib/main';

    const props = defineProps<{
        links: IssueStatisticsLink[];
        issueId: string;
        suppressQueryString: boolean;
        showAllLinks: boolean;
        loadAllLinks: boolean;
        isAnonymousTracking: boolean;
    }>();

    const emit = defineEmits<{
        (e: 'update:suppressQueryString', val: boolean): void;
        (e: 'update:showAllLinks', val: boolean): void;
        (e: 'update:loadAllLinks', val: boolean): void;
    }>();

    const router = useRouter();
    const route = useRoute();

    const linkTitles = ref<Record<string, string>>({});

    function decodeHtmlEntities(html: string) {
        const el = document.createElement('textarea');
        el.innerHTML = html;
        return el.value;
    }

    async function fetchLinkTitle(url: string) {
        if (linkTitles.value[url] !== undefined) return;
        try {
            const title = await api.get('/Download/Title?url=' + encodeURIComponent(url));
            if (title) linkTitles.value[url] = decodeHtmlEntities(title);
        } catch {}
    }

    watch(
        () => props.links,
        (links) =>
            links.forEach((link) => {
                if (!isWebVersion(link.Url)) fetchLinkTitle(link.Url);
            }),
        { immediate: true }
    );

    function shrinkLink(url: string) {
        return url.length > 100 ? url.substring(0, 100) + ' ...' : url;
    }

    function isWebVersion(url: string) {
        return url.indexOf('/' + props.issueId) !== -1;
    }

    function showLinkClicks(link: IssueStatisticsLink) {
        if (props.isAnonymousTracking) return;
        router.push({
            name: 'IssueStatisticsContacts',
            params: { issueId: route.params.issueId, contactType: 'ClickedLink' },
            query: { ...route.query, url: link.Url, suppressQueryString: String(props.suppressQueryString) },
        });
    }

    function showAll() {
        emit('update:showAllLinks', true);
        emit('update:loadAllLinks', true);
    }

    const orderOptions = () => {
        return [
            { display: $Resources.IssueStatisticsUniqueClicksHeading, key: 'ContactCount' },
            { display: $Resources.IssueStatisticsTotalClicksHeading, key: 'ClickCount' },
        ];
    };
</script>

<template>
    <div class="print-block">
        <h2 class="h3 m-0-b">
            {{ $Resources.IssueStatisticsLinksHeading }}
            <button
                type="button"
                class="popover-trigger popover-trigger-lg"
                v-popover-info="{ heading: $Resources.IssueStatisticsLinksHeading, body: $Resources.IssueStatisticsLinksIntroduction }">
                <span class="glyphicon glyphicon-info-sign" aria-hidden="true"></span>
            </button>
        </h2>

        <BsAlert
            class="m-sm-t"
            :show="links.length === 0"
            :type="AlertTypes.Info"
            :dismissible="false"
            :content="$Resources.IssueStatisticsLinksNoClicksMessage" />

        <template v-if="links.length > 0">
            <List
                class="th2-text-right"
                itemId="Url"
                display-as="table"
                :items="links"
                :table-headers="[
                    $Resources.IssueStatisticsLinksHeading,
                    $Resources.IssueStatisticsUniqueClicksHeading,
                    $Resources.IssueStatisticsTotalClicksHeading,
                ]"
                :filter-keys="['Url']"
                :orderOptions="orderOptions()">
                <template #toolbar>
                    <FormCheckable
                        class="m-sm-r"
                        :label="$Resources.SuppressQueryStringLabel"
                        :switch-style="SwitchStyle.Standard"
                        :checked="suppressQueryString"
                        @update:model-value="emit('update:suppressQueryString', $event)" />
                </template>
                <template #item="{ item }">
                    <td>
                        <template v-if="isWebVersion(item.Url)">
                            <a :href="item.Url" target="_blank" rel="noopener">{{ $Resources.IssueStatisticsLinkWebVersion }}</a>
                        </template>
                        <template v-else>
                            <a :href="item.Url" :title="item.Url" target="_blank" rel="noopener">{{ shrinkLink(item.Url) }}</a>
                            <template v-if="linkTitles[item.Url]">
                                <br />
                                {{ linkTitles[item.Url] }}
                            </template>
                        </template>
                    </td>
                    <td class="text-right strong">
                        <a v-if="!isAnonymousTracking" href="javascript:void(0)" @click="showLinkClicks(item as IssueStatisticsLink)">{{ item.ContactCount }}</a>
                        <span v-else>{{ item.ContactCount }}</span>
                    </td>
                    <td class="text-right strong">{{ item.ClickCount }}</td>
                </template>
                <template #footer>
                    <div class="form-group hidden-print" v-if="!(showAllLinks && loadAllLinks) && links.length >= 5">
                        <button class="btn btn-default btn-block" @click="showAll()">
                            {{ $Resources.LoadAllLinksButton }}
                        </button>
                    </div>
                </template>
            </List>
        </template>
    </div>
</template>
